/**
 * Utility functions for database object model fields mapping
 */

/**
 * Ensures the database_object_model_fields_mapping structure exists and is properly initialized
 * @param {Object} variableDialog - The variable dialog object
 * @param {number} objectNumber - The index of the object in the mapping array
 * @returns {Object} The initialized mapping object
 */
export const ensureMappingStructure = (variableDialog, objectNumber) => {
  // Handle null mappings array
  if (!variableDialog.database_object_model_fields_mapping) {
    variableDialog.database_object_model_fields_mapping = []
  } else if (Array.isArray(variableDialog.database_object_model_fields_mapping)) {
    // Clean null values from the array
    variableDialog.database_object_model_fields_mapping = variableDialog.database_object_model_fields_mapping
      .filter(item => item !== null && typeof item === 'object')
      .map(item => {
        // Ensure the item has required structure
        return {
          database_object_model_name: item.database_object_model_name || '',
          mappingType: item.mappingType || 'simple',
          data: Array.isArray(item.data) ? item.data.filter(d => d !== null && typeof d === 'object') : []
        }
      })
  }

  if (!variableDialog.database_object_model_fields_mapping[objectNumber]) {
    variableDialog.database_object_model_fields_mapping[objectNumber] = {
      database_object_model_name: '',
      mappingType: 'simple',
      data: []
    }
  }

  if (!variableDialog.database_object_model_fields_mapping[objectNumber].data) {
    variableDialog.database_object_model_fields_mapping[objectNumber].data = []
  }

  return variableDialog.database_object_model_fields_mapping[objectNumber]
}

/**
 * Ensures a specific data element exists in the mapping
 * @param {Object} variableDialog - The variable dialog object
 * @param {number} objectNumber - The index of the object in the mapping array
 * @param {number} elementKey - The index of the element in the data array
 * @returns {Object} The initialized data element
 */
export const ensureMappingDataElement = (variableDialog, objectNumber, elementKey) => {
  const mapping = ensureMappingStructure(variableDialog, objectNumber)

  if (!mapping.data[elementKey]) {
    mapping.data[elementKey] = {
      database_object_model_field_name: '',
      variable_field_name: '',
    }
  }

  return mapping.data[elementKey]
}

/**
 * Gets the current mapping value safely
 * @param {Object} variableDialog - The variable dialog object
 * @param {number} objectNumber - The index of the object in the mapping array
 * @param {number} elementKey - The index of the element in the data array (optional)
 * @returns {string} The database_object_model_field_name value
 */
export const getMappingValue = (variableDialog, objectNumber, elementKey = null) => {
  if (!variableDialog.database_object_model_fields_mapping?.[objectNumber]) {
    return ''
  }

  if (elementKey !== null) {
    if (!variableDialog.database_object_model_fields_mapping[objectNumber].data?.[elementKey]) {
      return ''
    }
    return variableDialog.database_object_model_fields_mapping[objectNumber].data[elementKey].database_object_model_field_name || ''
  }

  return variableDialog.database_object_model_fields_mapping[objectNumber].database_object_model_name || ''
}

/**
 * Initializes mapping for unisign/multisign types (needs elements 1 and 2)
 * @param {Object} variableDialog - The variable dialog object
 */
export const initializeUnisignMapping = (variableDialog) => {
  const mapping = ensureMappingStructure(variableDialog, 0)

  // Ensure elements 1 and 2 exist for unisign/multisign
  if (!mapping.data[1]) {
    mapping.data[1] = {
      database_object_model_field_name: '',
      variable_field_name: '',
    }
  }

  if (!mapping.data[2]) {
    mapping.data[2] = {
      database_object_model_field_name: '',
      variable_field_name: '',
    }
  }
}
